import React from "react";
import { useTheme } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import { Box, Typography } from "@material-ui/core";
import moment from "moment-timezone";
import { getCampaignTimezoneSync } from "../../../helpers/getCampaignTimezone";

const CampaignAlertModals = ({
  // Error modal
  errorModalOpen,
  setErrorModalOpen,
  errorMessage,
  setErrorMessage,
  // Date-past modal
  datePastModalOpen,
  setDatePastModalOpen,
  datePastMessage,
  pendingSetFieldValue,
  setPendingSetFieldValue,
  pendingSaveValues,
  setPendingSaveValues,
  saveCampaignRef,
  // Format-invalid modal
  formatInvalidModalOpen,
  setFormatInvalidModalOpen,
  formatInvalidMessage,
  // Size-exceeded modal
  sizeExceededModalOpen,
  setSizeExceededModalOpen,
  sizeExceededMessage,
  // Short-time modal
  shortTimeModalOpen,
  setShortTimeModalOpen,
  shortTimeMessage,
  setShortTimeMessage,
  pendingScheduledAt,
  setPendingScheduledAt,
}) => {
  const theme = useTheme();

  return (
    <>
      {/* ✅ MEJORA UX: Modal de error mejorado para notificaciones importantes */}
      <Dialog
        open={errorModalOpen}
        onClose={() => {
          setErrorModalOpen(false);
          // ✅ CORRECCIÓN: Limpiar el mensaje de error al cerrar el modal
          setErrorMessage("");
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle style={{
          backgroundColor: theme.palette.error.main,
          color: theme.palette.error.contrastText,
          padding: theme.spacing(2),
          display: 'flex',
          alignItems: 'center',
          gap: theme.spacing(1)
        }}>
          <span role="img" aria-label="Error" style={{ fontSize: '1.5rem' }}>⚠️</span>
          <span style={{ fontWeight: 'bold' }}>Error de Validación</span>
        </DialogTitle>
        <DialogContent style={{
          padding: theme.spacing(3),
          paddingTop: theme.spacing(2)
        }}>
          <Typography
            component="div"
            style={{
              whiteSpace: 'pre-line',
              fontSize: '1rem',
              lineHeight: '1.8',
              color: theme.palette.text.primary,
              padding: theme.spacing(2),
              backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 0, 0, 0.1)' : 'rgba(255, 0, 0, 0.05)',
              borderRadius: '4px',
              border: `1px solid ${theme.palette.error.light}`,
              fontFamily: 'inherit'
            }}
          >
            {errorMessage || "Ocurrió un error al guardar la campaña."}
          </Typography>

          {/* ✅ MEJORA: Mostrar información adicional si es conflicto de horarios */}
          {errorMessage && (errorMessage.includes("Horario no disponible") || errorMessage.includes("Conflicto con") || errorMessage.includes("Siguiente disponible")) && (
            <Box style={{
              marginTop: theme.spacing(2),
              padding: theme.spacing(2),
              backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 193, 7, 0.1)' : 'rgba(255, 193, 7, 0.1)',
              borderRadius: '4px',
              border: `1px solid ${theme.palette.warning.light}`
            }}>
              <Typography variant="body2" style={{
                color: theme.palette.warning.dark,
                fontWeight: 'bold',
                marginBottom: theme.spacing(1)
              }}>
                <span role="img" aria-label="Sugerencia">💡</span> Sugerencia:
              </Typography>
              <Typography variant="body2" style={{
                color: theme.palette.text.secondary,
                lineHeight: '1.6'
              }}>
                Por favor, ajusta la fecha y hora programada para evitar conflictos con otras campañas activas.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions style={{
          padding: theme.spacing(2),
          borderTop: `1px solid ${theme.palette.divider}`
        }}>
          <Button
            onClick={() => {
              setErrorModalOpen(false);
              // ✅ CORRECCIÓN: Limpiar el mensaje de error al cerrar el modal
              setErrorMessage("");
            }}
            color="primary"
            variant="contained"
            style={{
              minWidth: '120px',
              fontWeight: 'bold'
            }}
          >
            Entendido
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={datePastModalOpen}
        onClose={() => {
          setDatePastModalOpen(false);
          setPendingSetFieldValue(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <span role="img" aria-label="Fecha pasada">📅</span> Fecha en el pasado
        </DialogTitle>
        <DialogContent>
          <Typography style={{ whiteSpace: 'pre-line' }}>{datePastMessage}</Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setDatePastModalOpen(false);
              setPendingSetFieldValue(null);
            }}
            color="default"
            variant="outlined"
          >
            Cancelar
          </Button>
          <Button
            onClick={() => {
              // ✅ ACEPTAR: Actualizar fecha a hora actual + 2 minutos
              const timezone = getCampaignTimezoneSync();
              const nowInCompanyTimezone = moment().tz(timezone);
              // Calcular hora actual + 2 minutos
              const newScheduledAt = nowInCompanyTimezone.add(2, 'minutes').format('YYYY-MM-DD HH:mm:ss');

              if (pendingSetFieldValue) {
                // Actualizar el campo en el formulario
                pendingSetFieldValue('scheduledAt', newScheduledAt);
              }

              // Si hay valores pendientes de guardado, actualizar scheduledAt y continuar
              if (pendingSaveValues && saveCampaignRef.current) {
                const updatedValues = { ...pendingSaveValues, scheduledAt: newScheduledAt };
                setPendingSaveValues(null);
                setDatePastModalOpen(false);
                setPendingSetFieldValue(null);
                // Continuar con el guardado (saltando validación de fecha pasada y tiempo corto)
                setTimeout(() => {
                  saveCampaignRef.current(updatedValues, true, true);
                }, 100);
              } else {
                setDatePastModalOpen(false);
                setPendingSetFieldValue(null);
                setPendingSaveValues(null);
              }
            }}
            color="primary"
            variant="contained"
          >
            Aceptar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={formatInvalidModalOpen}
        onClose={() => setFormatInvalidModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <span role="img" aria-label="Formato inválido">⚠️</span> Formato de archivo no permitido
        </DialogTitle>
        <DialogContent>
          <Typography style={{ whiteSpace: 'pre-line' }}>{formatInvalidMessage}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFormatInvalidModalOpen(false)} color="primary" variant="contained">
            Aceptar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={sizeExceededModalOpen}
        onClose={() => setSizeExceededModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <span role="img" aria-label="Tamaño excedido">⚠️</span> Tamaño de archivo excedido
        </DialogTitle>
        <DialogContent>
          <Typography style={{ whiteSpace: 'pre-line' }}>{sizeExceededMessage}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSizeExceededModalOpen(false)} color="primary" variant="contained">
            Aceptar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={shortTimeModalOpen}
        onClose={() => {
          setShortTimeModalOpen(false);
          setShortTimeMessage("");
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <span role="img" aria-label="Tiempo corto">⏰</span> Tiempo de programación muy corto
        </DialogTitle>
        <DialogContent>
          <Typography style={{ whiteSpace: 'pre-line' }}>{shortTimeMessage}</Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              // ✅ CANCELAR: Cerrar modal sin hacer nada
              setShortTimeModalOpen(false);
              setShortTimeMessage("");
              setPendingScheduledAt(null);
            }}
            color="default"
            variant="outlined"
          >
            Cancelar
          </Button>
          <Button
            onClick={async () => {
              // ✅ ENTENDIDO: Aceptar la hora seleccionada y continuar
              const pendingSetFieldValueFn = pendingSetFieldValue;
              const scheduledAtValue = pendingScheduledAt;
              const pendingValues = pendingSaveValues;
              const saveFn = saveCampaignRef.current;

              // Cerrar modal
              setShortTimeModalOpen(false);
              setShortTimeMessage("");

              // Si viene desde onChange del campo, actualizar el campo
              if (pendingSetFieldValueFn && scheduledAtValue) {
                pendingSetFieldValueFn('scheduledAt', scheduledAtValue);
              }

              // Si viene desde handleSaveCampaign (botón AGREGAR), continuar con el guardado
              if (saveFn && pendingValues) {
                // Continuar con el guardado saltando la validación de tiempo corto
                setTimeout(() => {
                  saveFn(pendingValues, false, true); // shouldSkipShortTimeCheck = true
                }, 100);
              }

              // Limpiar valores pendientes
              setPendingScheduledAt(null);
            }}
            color="primary"
            variant="contained"
          >
            Entendido
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CampaignAlertModals;
